import { ArrowRight, BarChart3, Clock, FileText, PlusCircle, Users, CheckCircle } from 'lucide-react'
import { Button, Card, CardHeader, CardTitle, CardContent, Badge } from '../ui'
import StatsGrid from './StatsGrid'
import { useGerantDashboard } from '../../hooks/useGerantDashboard'
import { useNavigate } from 'react-router-dom'
import storage from '../../utils/storage'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis, ResponsiveContainer } from 'recharts'

const statusConfig = {
  Actif: { label: "Actif", className: "bg-success/10 text-success border-success/20" },
  Inactif: { label: "Brouillon", className: "bg-muted text-muted-foreground border-muted" },
  Clôturé: { label: "Clôturé", className: "bg-accent/10 text-accent border-accent/20" },
};


const GerantDashboard = () => {
    const navigate = useNavigate()
    const user = storage.getUser();
    const userName = user?.first_name || user?.name || user?.username || 'Administrateur';
    const { kpis, surveys } = useGerantDashboard();
 
    const stats = [
        {
            icon: FileText,
            label: 'Sondages Actifs',
            value: kpis?.active_surveys || 0,
            change: kpis?.users_change || '+0',
            changeType: 'positive',
            variant: "primary",
            details: `En cours`
        },
        {
            icon: Users,
            label: 'Participants',
            value: kpis?.total_participants || 0,
            change: kpis?.surveys_change || '+0',
            changeType: 'positive',
            variant: "accent",
            details: `${kpis?.participation_rate || 0}% de participation`,
        },
        {
            icon: BarChart3,
            label: 'Total réponses',
            value: kpis?.responses_received || 0,
            change: kpis?.responses_change || '+0',
            changeType: 'positive',
            variant: "success",
            details: `Collectées`
        },
        {
            icon: CheckCircle,
            label: 'Sondages Fermés',
            value: `${kpis?.closed_surveys || 0}`,
            change: kpis?.participation_change || '+0%',
            changeType: 'positive',
            variant: "primary",
            details: 'Archivés',
        },
    ];

    // Créer un graphique basé sur les réponses des sondages récents
    const chartData = surveys && Array.isArray(surveys) ? surveys.slice(0, 7).map((survey, idx) => ({
      name: survey.title?.substring(0, 10) || `Sondage ${idx + 1}`,
      réponses: survey.responses_count || 0
    })) : [];
  return (
    <div className='space-y-6'>
         {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#009688]">
              Bonjour <span className='lowercase'>{userName}</span> !
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Bienvenue sur votre espace de gestion
            </p>
          </div>
          <Button onClick={() => navigate("/gerant/gestion-sondages/nouveau")} className="gap-2 font-semibold">
            <PlusCircle className="h-4 w-4" />
            Nouveau sondage
          </Button>
        </div>
        <StatsGrid stats={stats} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <Card className="lg:col-span-2 shadow-card border-0">
            <CardHeader className="pb-2">
              <CardTitle className="font-display text-lg">Réponses par sondage</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData && chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                      }}
                    />
                    <Bar dataKey="réponses" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  <p>Aucun sondage disponible</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent surveys */}
          <Card className="shadow-card border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-display text-lg">Sondages récents</CardTitle>
                <Button variant="ghost" size="sm" className="text-primary gap-1" onClick={() => navigate("/gerant/gestion-sondages")}>
                  Voir tout <ArrowRight className="h-3 w-3" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {surveys.map((survey) => {
                const status = statusConfig[survey.computed_status] || { label: survey.computed_status, className: "bg-gray-100 text-gray-700" };
                return (
                  <div
                  onClick={() => navigate(`/gerant/gestion-sondages/${survey.id}`)}
                    key={survey.id}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group"
                  >
                    <div className="space-y-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
                        {survey.title}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={status.className}>
                          {status.label}
                        </Badge>
                        {survey.computed_status !== "draft" && (
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {survey.responses_count}/{survey.target_responses}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
    </div>
  )
}

export default GerantDashboard